import React, { useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
export interface NavigationSection {
  id: string;
  label: string;
  icon: React.ReactNode;
}
interface SettingsLayoutProps {
  title: string;
  subtitle: string;
  headerIcon: React.ReactNode;
  navigationSections: NavigationSection[];
  activeSection: string;
  setActiveSection: (id: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  showSaveButton: boolean;
  children: React.ReactNode;
}
export const SettingsLayout: React.FC<SettingsLayoutProps> = ({
  title,
  subtitle,
  headerIcon,
  navigationSections,
  activeSection,
  setActiveSection,
  onSubmit,
  showSaveButton,
  children
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const activeSectionData = navigationSections.find(s => s.id === activeSection);
  return <div className={`min-h-screen ${dk.page}`} data-id="element-594" data-mp="3mx356nil0bpu">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-595" data-mp="3mvs10ml851y7">
        {/* Page Header */}
        <div className="flex items-center mb-8" data-id="element-596" data-mp="3hx78zag4loku">
          <div className={`p-3 rounded-xl mr-4 ${rt.bgMedium}`} data-id="element-597" data-mp="hie6hwy01zj">
            {headerIcon}
          </div>
          <div data-id="element-598" data-mp="3rq5zwyz3hr7m">
            <h1 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-599" data-mp="3rpmsic72ml7u">{title}</h1>
            <p className={`text-sm mt-1 ${dk.textMuted}`} data-id="element-600" data-mp="3401wosptjth5">{subtitle}</p>
          </div>
        </div>

        {/* Mobile Collapsible Section Picker */}
        <div className={`md:hidden mb-6 rounded-xl border shadow-sm overflow-hidden ${dk.card}`} data-id="element-601" data-mp="3czypn6j8rei0">
          {/* Active section header — tap to toggle */}
          <button onClick={() => setMobileNavOpen(!mobileNavOpen)} className={`w-full flex items-center justify-between px-4 py-3.5 text-sm font-medium ${rt.bgLight} ${rt.textPrimary}`} data-id="element-602" data-mp="38x25vchytcch">
            <div className="flex items-center" data-id="element-603" data-mp="3h2lb79zfzx56">
              {activeSectionData && <span className={`mr-3 ${rt.textPrimary}`} data-id="element-604" data-mp="3h4z2r8uwyr7z">
                  {activeSectionData.icon}
                </span>}
              {activeSectionData?.label || 'Select Section'}
            </div>
            <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${mobileNavOpen ? 'rotate-180' : ''} ${rt.textPrimary}`} data-id="element-605" data-mp="3v246h275ne02" />
          </button>

          {/* Expandable section list */}
          {mobileNavOpen && <nav data-id="element-606" data-mp="3393nre3my09v">
              <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-607" data-mp="37ct9zwwu85rq">
                {navigationSections.filter(section => section.id !== activeSection).map(section => <li key={section.id} data-id="element-608" data-mp="2fmf3ilr5lvq3">
                      <button onClick={() => {
                setActiveSection(section.id);
                setMobileNavOpen(false);
              }} className={`w-full flex items-center justify-between px-4 py-3.5 text-sm font-medium transition-colors ${dk.textSecondary}`} data-id="element-609" data-mp="2fldjvsw2o0wr">
                        <div className="flex items-center" data-id="element-610" data-mp="231ot8x05yakg">
                          <span className={`mr-3 ${dk.textFaint}`} data-id="element-611" data-mp="1y46t9w44j5ol">
                            {section.icon}
                          </span>
                          {section.label}
                        </div>
                        <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 ${dk.textFaint}`} data-id="element-612" data-mp="1odvefeiwjc2x" />
                      </button>
                    </li>)}
              </ul>
            </nav>}
        </div>

        <div className="flex flex-col md:flex-row gap-8" data-id="element-613" data-mp="1ae2uc21vru3x">
          {/* Desktop Sidebar */}
          <div className="hidden md:block w-64 flex-shrink-0" data-id="element-614" data-mp="15f9c5wbnfexf">
            <div className={`sticky top-24 rounded-xl border shadow-sm p-4 ${dk.card}`} data-id="element-615" data-mp="15g1a3dspxoqv">
              <nav data-id="element-616" data-mp="35jv43x4cdrtf">
                <ul className="space-y-1" data-id="element-617" data-mp="3agc4pqsq9yne">
                  {navigationSections.map(section => <li key={section.id} data-id="element-618" data-mp="2vrz2wd6yllkz">
                      <button onClick={() => setActiveSection(section.id)} className={`w-full flex items-center px-3 py-2.5 text-sm rounded-lg transition-colors ${activeSection === section.id ? `${rt.activeTab} border-l-2 ${rt.borderActive}` : `${dk.textSecondary}`}`} data-id="element-619" data-mp="2vr67279jgghm">
                        <span className={`mr-3 ${activeSection === section.id ? rt.textPrimary : dk.textFaint}`} data-id="element-620" data-mp="2cstlfm1fxe55">
                          {section.icon}
                        </span>
                        {section.label}
                      </button>
                    </li>)}
                </ul>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0" data-id="element-621" data-mp="20jdlvoirxmvq">
            <form onSubmit={onSubmit} data-id="element-622" data-mp="1vjiz9x617jgh">
              <div className={`rounded-xl border shadow-sm overflow-hidden ${dk.card}`} data-id="element-623" data-mp="1vkk8dw3hlvvk">
                {children}

                {/* Save Button */}
                {showSaveButton && <div className={`px-6 py-4 border-t ${dk.border} flex justify-end gap-3`} data-id="element-624" data-mp="25g4169mr8eg3">
                    <button type="button" className={`py-2 px-4 text-sm font-medium rounded-lg border ${dk.card}`} data-id="element-625" data-mp="2ea0p9tzhc0y5">
                      Cancel
                    </button>
                    <button type="submit" className={`py-2 px-4 text-sm font-medium rounded-lg ${rt.primaryButton}`} data-id="element-626" data-mp="2o980aaiqrji3">
                      Save Changes
                    </button>
                  </div>}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>;
};